import type { Product } from "@/types/firestore";

/**
 * Variações e adicionais de um produto, só para conferência no painel. Com
 * variações, o produto não é uma opção: o cliente escolhe uma variação, cada
 * uma com o próprio preço (a gravada sem preço, de antes de ele ser
 * obrigatório, vale o do produto). A variação desativada (`isAvailable:
 * false`) não conta no preço. A regra completa (herança, grupos de
 * adicionais, limites, conta da linha do pedido) fica em
 * `domain/utils/productOptions.ts` dos dois aplicativos.
 */

const isPrice = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

/**
 * O menor e o maior preço entre as opções: as variações ativas (todas, quando
 * nenhuma está ativa), ou o produto sem elas.
 */
export function productPriceRange(product: Product): { min: number; max: number } | null {
  const variations = product.variations ?? [];
  const active = variations.filter((variation) => variation.isAvailable !== false);
  const priced = active.length > 0 ? active : variations;
  const prices = (
    priced.length > 0
      ? priced.map((variation) => (isPrice(variation.price) ? variation.price : product.price))
      : [product.price]
  ).filter(isPrice);
  if (prices.length === 0) return null;
  return { min: Math.min(...prices), max: Math.max(...prices) };
}

/**
 * Quantas variações, quantos adicionais escolhidos da lista geral (contando
 * os de cada variação; no produto de antes dela, os da cópia), quantos grupos
 * de adicionais ligados e, no combo, quantos itens a lista comum tem (`null`
 * fora do combo).
 */
export function productOptionCounts(product: Product): {
  variations: number;
  addons: number;
  addonGroups: number;
  compositeItems: number | null;
} {
  const variations = product.variations ?? [];
  const idsOf = (refIds: string[] | undefined, legacy: { id: string }[] | undefined) =>
    refIds ?? (legacy ?? []).map((addon) => addon.id);
  const addonIds = new Set(idsOf(product.addonIds, product.addons));
  const groupIds = new Set(product.addonGroupIds ?? []);
  if (product.addonsPerVariation) {
    variations.forEach((variation) => {
      idsOf(variation.addonIds, variation.addons).forEach((addonId) => addonIds.add(addonId));
      (variation.addonGroupIds ?? []).forEach((groupId) => groupIds.add(groupId));
    });
  }
  return {
    variations: variations.length,
    addons: addonIds.size,
    addonGroups: groupIds.size,
    compositeItems: product.isComposite === true ? (product.compositeItems ?? []).length : null,
  };
}
